import { useState, useCallback } from 'react';
import { useDispatch } from 'src/store';
import { update } from 'src/store/auth';
import {
  startAskSalahFreeTrial,
  createAskSalahOrder,
  generateAskSalahToken,
  getUserAskSalahSubscription,
} from 'src/api/askSalah';
import type {
  AskSalahBillingCycle,
} from 'src/types/askSalah';

/**
 * Hook for subscription mutations (subscribe, upgrade, cancel)
 * 
 * Returns:
 * - startTrial: Function to activate free trial
 * - subscribe: Function to subscribe to paid plan
 * - generateToken: Function to generate access token
 * - isProcessing: Loading state
 * - error: Error state
 */
export const useAskSalahSubscriptionActions = () => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Start Ask Salah free trial
   */
  const startTrial = useCallback(async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const result = await startAskSalahFreeTrial();

      if (result.success) {
        const askSalahSubscription = await getUserAskSalahSubscription();
        if (askSalahSubscription) {
          dispatch(
            update({
              user: { askSalahSubscription } as any,
            }),
          );
        }
      }

      return { success: true, message: result.message };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to start free trial';
      setError(errorMessage);
      console.error('Error starting trial:', err);
      return { success: false, error: errorMessage };
    } finally {
      setIsProcessing(false);
    }
  }, [dispatch]);

  /**
   * Subscribe to Ask Salah paid plan (Basic or Premium)
   */
  const subscribe = useCallback(
    async (
      planType: 'basic' | 'premium',
      billingCycle: AskSalahBillingCycle,
      quotationId?: number,
    ) => {
      try {
        setIsProcessing(true);
        setError(null);

        const subscription = await createAskSalahOrder({
          quotationId,
          planType,
          billingCycle,
          paymentMethod: 'card',
          gateway: 'tap',
        });

        dispatch(
          update({
            user: {
              askSalahSubscription: subscription,
            } as any,
          })
        );

        return { success: true, subscription };
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to subscribe';
        setError(errorMessage);
        console.error('Error subscribing:', err);
        return { success: false, error: errorMessage };
      } finally {
        setIsProcessing(false);
      }
    },
    [dispatch]
  );

  /**
   * Generate one-time token for Ask Salah access
   */
  const generateToken = useCallback(async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const result = await generateAskSalahToken();

      return { success: true, token: result.token };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to generate access token';
      setError(errorMessage);
      console.error('Error generating token:', err);
      return { success: false, error: errorMessage };
    } finally {
      setIsProcessing(false);
    }
  }, []);

  /**
   * Generate token and redirect to Ask Salah external site
   * @param existingWindow - Optional pre-opened window reference (to avoid popup blockers on mobile)
   */
  const accessAskSalah = useCallback(async (existingWindow?: Window | null) => {
    try {
      // Create loading page HTML with spinner
      const loadingHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Loading AskSalah...</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body { 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              height: 100vh; 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            }
            .loader { 
              text-align: center; 
              color: white;
            }
            .spinner {
              border: 4px solid rgba(255, 255, 255, 0.3);
              border-top: 4px solid white;
              border-radius: 50%;
              width: 50px;
              height: 50px;
              animation: spin 1s linear infinite;
              margin: 0 auto 24px;
            }
            .text {
              font-size: 18px;
              font-weight: 500;
              letter-spacing: 0.5px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="loader">
            <div class="spinner"></div>
            <p class="text">Opening AskSalah...</p>
          </div>
        </body>
        </html>
      `;

      // Use provided window or open new one
      let newWindow = existingWindow;
      
      if (!newWindow) {
        // Open new window with loading page (synchronously to avoid popup blockers)
        newWindow = window.open('', '_blank');
      }
      
      if (!newWindow) {
        console.warn('[accessAskSalah] Popup blocked, falling back to current window navigation');
        // Fallback: if popup was blocked, we'll navigate current window
        // but still generate token first
      } else {
        // Write loading page to newly opened window
        newWindow.document.write(loadingHTML);
        newWindow.document.close();
      }

      // Now generate the token (async operation)
      const result = await generateToken();

      if (result.success && result.token) {
        const askSalahUrl = `https://asksalah.smartsway.com/authenticate?sal_token=${result.token}`;
        
        if (newWindow && !newWindow.closed) {
          // Navigate the already-opened window to AskSalah
          newWindow.location.href = askSalahUrl;
        } else {
          // Fallback: navigate current window if popup was blocked or window was closed
          window.location.href = askSalahUrl;
        }
        return { success: true };
      }

      // If token generation failed, close the window
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }

      // If no token but no error, something went wrong
      return { 
        success: false, 
        error: result.error || 'Failed to generate access token' 
      };
    } catch (err: any) {
      console.error('[accessAskSalah] Error:', err);
      return { 
        success: false, 
        error: err.message || 'Failed to access Ask Salah' 
      };
    }
  }, [generateToken]);

  return {
    startTrial,
    subscribe,
    generateToken,
    accessAskSalah,
    isProcessing,
    error,
    clearError: () => setError(null),
  };
};


